import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

const DIFFERENTIATORS = [
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    ),
    color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
    title: 'Built for the moment of placement',
    body: "Other directories are designed for patients looking on their own. This one is built for case managers — fast clinical filters, bed type breakdowns, distance sorting, and contact info front and center. Every feature is designed for the workflow of placing a client right now.",
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4 6h16M4 10h16M4 14h8" />
    ),
    color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/30',
    title: 'Granular bed-type detail',
    body: 'Knowing a facility "has inpatient" isn\'t enough. This tool shows exactly what kind — withdrawal management, CLIP beds, competency restoration, MH peer respite, intensive BH treatment, and more — so you can match the level of care to the client\'s needs.',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
    color: 'text-green-600 bg-green-50 dark:bg-green-900/30',
    title: 'Live "Accepting Referrals" signal',
    body: "Facilities can flip a green/red toggle directly on their own listing. No more calling ahead just to find out they're full. The referral-ready indicator is visible on every card — a single glance tells you whether to make the call.",
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    ),
    color: 'text-teal-600 bg-teal-50 dark:bg-teal-900/30',
    title: 'Facilities maintain their own profile',
    body: 'Verified agency accounts can add a point of contact, program description, languages spoken, telehealth availability, referral requirements, and self-reported bed counts. It\'s a living directory — not a static spreadsheet screenshot.',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30',
    title: 'Official WA DOH data, synced monthly',
    body: 'All facility data comes from the Washington State Department of Health Greenbook — the official credentialing source the state uses for licensed facility records. A smart sync runs monthly so additions, closures, and changes are always reflected.',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    ),
    color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30',
    title: 'Private notes — yours, on every card',
    body: 'Add case notes directly to any facility card — intake contacts, response times, what worked, what didn\'t. Notes are private to your account and persist across sessions. Real clinician knowledge, not just database fields.',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    ),
    color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/30',
    title: 'Washington-only — no noise',
    body: "Designed exclusively for WA case managers doing WA placements. Filter by county, distance from any ZIP code, population served, and service type — all tuned to how behavioral health referrals actually work in this state.",
  },
];

const COMING_SOON = [
  {
    title: 'Statewide crisis-system integration',
    body: "Connecting facility-reported bed availability to Washington's 988 crisis network and regional crisis lines, so the people placing clients in a crisis see the same live picture facilities maintain here.",
  },
  {
    title: 'Mobile app with click-to-call',
    body: 'A React Native version of the tool for field use — tap to call, tap to email, works offline with a cached facility list.',
  },
];

export default function WhyDifferent() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Helmet>
        <title>What Makes This Different | WA BHA Treatment Finder</title>
        <meta name="description" content="Why WA BHA Treatment Finder is different from other behavioral health directories — built by a clinician, for case managers." />
      </Helmet>
      <Header right={null} />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        <div className="mb-8">
          <Link to="/faq" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to FAQ
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            What makes this tool different?
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
            Most behavioral health directories are lists. This is a clinical placement tool — built around how case managers actually work.
          </p>
        </div>

        {/* Origin story */}
        <div className="mb-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-5 flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Built by someone who lived the problem</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              This tool was created by a Washington State behavioral health professional who spent years placing clients into treatment — and got tired of dead ends, outdated lists, and phone calls that went nowhere. Every feature exists because it was actually needed in the field, not because it looked good on a product roadmap.
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {DIFFERENTIATORS.map(({ icon, color, title, body }) => (
            <div
              key={title}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 flex gap-4"
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {icon}
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* What's coming */}
        <div className="mt-10">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">What's coming</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            This is an actively developed tool. Here's what's on the roadmap.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {COMING_SOON.map(({ title, body }) => (
              <div
                key={title}
                className="bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400 inline-block flex-shrink-0"></span>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-0.5">Ready to try it?</p>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Create a free account for full facility details, all filters, and bed availability data.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <Link
              to="/signup"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              Create free account
            </Link>
            <Link
              to="/"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center"
            >
              Browse first →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
